import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Radio,
  MenuItem,
  Select,
  TextareaAutosize,
  Typography,
  TablePagination,
  TextField,
  DialogActions,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Popover,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { RadioButtonChecked, RadioButtonUnchecked } from "@mui/icons-material";
import TableChartIcon from '@mui/icons-material/TableChart';
import Papa from "papaparse";
import "../../css/OutPutDirectory.css";
import { setDataMode, setCsvData, updateCsvData, updateUniqueField, deleteCsvRow } from '../../redux/slices/CSVDataSlice';
import { toast } from 'react-toastify';
import CloseIcon from "@mui/icons-material/Close";
import { FORM_KEYS } from "../../utills/constant";
import { saveFormDetails } from "../../redux/slices/CreateAgentSlice";

const CsvFileScreen = () => {
  const fileInputRef = useRef(null);
  const selectRef = React.useRef(null);
  const { csvData, dataMode } = useSelector((state) => state.CSVDataSlice);
  const {
    formDetails
  } = useSelector((state) => state.CreateAgentSlice);
  const dispatch = useDispatch();
  const [newRowData, setNewRowData] = useState({
    Unique: '',
    Checksum: '',
    Keys: '',
    Description: '',
    Format: 'String',
    Fields: '',
    Fields_Description: ''
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDataDialog, setOpenAddDataDialog] = useState(false);
  const [openAddSubfieldsDialog, setOpenAddSubfieldsDialog] = useState(false);
  const [dialogData, setDialogData] = useState([]);
  const [dialogRowIndex, setDialogRowIndex] = useState(null);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [processingLevel, setProcessingLevel] = useState('level 1');
  const [anchorEl, setAnchorEl] = useState(null);

  const csvHeaders = {
    Unique: "Unique identifier for the record",
    Checksum: "Checksum value for data integrity",
    Keys: "Output key used for reference",
    Description: "Details about the key",
    Format: "Expected format (e.g., string, integer, date)",
    Actions: "Edit & Delete a row"
  };

  const formatOptions = ["String", "Boolean", "Enum", "Nest"];

  const getCellStyle = (index) => {
    switch (index) {
      case 0:
        return { width: 100, fontSize: '14px', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 1:
        return { width: 120, fontSize: '14px', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 2:
        return { width: 180, fontSize: '14px', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 3:
        return { width: 500, fontSize: '14px', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 4:
        return { width: 100, fontSize: '14px', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 5:
        return { width: 100, fontSize: '14px', fontFamily: "'Poppins', sans-serif", color: 'white' };
      default:
        return { padding: "8px" };
    }
  };

  useEffect(() => {
    if (!formDetails?.processing_options) {
      dispatch(saveFormDetails({ key: FORM_KEYS.PROCESSING_OPTIONS, value: "level 1" }));
    }
  }, [dispatch, formDetails?.processing_options]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (file && file.type === "text/csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: function (results) {
          let newRows = results.data;
          let uniqueFound = false;
          newRows = newRows.map((row) => {
            if (row.Unique === "1") {
              if (!uniqueFound) {
                uniqueFound = true;
                return row;
              }
              return { ...row, Unique: "" };
            }
            return row;
          });

          dispatch(setCsvData(newRows));
          dispatch(setDataMode("upload"));

          setPage(0);
          toast.success("CSV Uploaded Successfully");
        },
      });
    } else {
      toast.error("Please upload a valid CSV file");
    }
  };

  const handleExportCSV = () => {
    const defaultHeaders = [
      "Unique",
      "Checksum",
      "Keys",
      "Description",
      "Format",
      "Fields",
      "Fields_Description"
    ];

    const dataToExport = csvData && csvData.length > 0
      ? csvData
      : [Object.fromEntries(defaultHeaders.map((header) => [header, ""]))];

    const csv = Papa.unparse(dataToExport, {
      columns: defaultHeaders
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'keys.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDragAndDrop = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleOpenDialog = (row, index) => {
    const fieldsArray = row.Fields.split(',');
    const descriptionsArray = row.Fields_Description.split(',');
    const dialogData = fieldsArray.map((field, i) => ({
      field: field.trim(),
      description: descriptionsArray[i]?.trim() || ''
    }));
    setDialogData(dialogData);
    setDialogRowIndex(index);
    setOpenDialog(true);
  };

  const handleAddRow = () => {
    setRows([...rows, { field: '', description: '' }]);
  };

  const handleRemoveRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const handleSaveNewSubFields = () => {
    const hasEmptyField = rows.some((r) => !r.field || r.field.trim() === "");

    if (hasEmptyField) {
      toast.error("Fields should not be empty");
      return;
    }

    const fields = rows.map((r) => r.field).join(",");
    const fieldsDescription = rows.map((r) => r.description).join(",");

    setNewRowData((prev) => ({
      ...prev,
      Fields: fields,
      Fields_Description: fieldsDescription,
    }));

    setOpenAddSubfieldsDialog(false);
  };

  const handleSaveFields = () => {
    const cleanedData = dialogData.filter(
      (d) => d.field.trim() !== '' || d.description.trim() !== ''
    );

    const updatedFields = cleanedData.map((d) => d.field.trim()).join(', ');
    const updatedDescriptions = cleanedData.map((d) => d.description.trim()).join(', ');

    dispatch(updateCsvData({
      index: dialogRowIndex,
      key: 'Fields',
      value: updatedFields,
    }));

    dispatch(updateCsvData({
      index: dialogRowIndex,
      key: 'Fields_Description',
      value: updatedDescriptions,
    }));

    setOpenDialog(false);
    setEditingRowIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setEditingRowIndex(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = () => {
    if (deleteIndex !== null && deleteIndex !== undefined) {
      dispatch(deleteCsvRow(deleteIndex));
      if (editingRowIndex === deleteIndex) setEditingRowIndex(null);
      toast.success('Record Removed Successfully');
    } else {
      toast.error('Unable to delete row. Please try again.');
    }
    setOpenConfirmDialog(false);
    setDeleteIndex(null);
    setPage(0);
  };

  const filteredData = csvData
    .map((row, i) => ({ ...row, originalIndex: i }))
    .filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchText.toLowerCase())
      )
    );

  // Empty state component
  const EmptyState = () => (
    <Card
      sx={{
        mt: 2,
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        border: '2px dashed #cbd5e1',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: '#1976D2',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <CardContent sx={{
        textAlign: 'center',
        py: 6,
        px: 4
      }}>
        <Box sx={{ mb: 3 }}>
          <TableChartIcon
            sx={{
              fontSize: 80,
              color: '#94a3b8',
              mb: 2
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: '#475569',
            mb: 2
          }}
        >
          No Data Available
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#64748b',
            maxWidth: 400,
            mx: 'auto'
          }}
        >
          Get started by uploading a CSV file or adding your first row to begin managing your data fields.
        </Typography>
      </CardContent>
    </Card>
  );

  const handleProcessingChange = (event) => {
    setProcessingLevel(event.target.value);
    setAnchorEl(selectRef.current);
    dispatch(saveFormDetails({ key: FORM_KEYS.PROCESSING_OPTIONS, value: event.target.value }))
  };

  console.log("formdetails", formDetails)

  const handleMetadataChange = (event) => {
    dispatch(
      saveFormDetails({
        key: FORM_KEYS.META_DATA_ANALYSER,
        value: event.target.checked,
      })
    );
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "processing-options-popover" : undefined;

  // Popover content based on selection
  const getPopoverContent = () => {
    if (processingLevel === "level 1") {
      return (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 1 }}
          >
            Quick: PDF → Extract
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Only PDF gets uploaded to Gemini" />
            </ListItem>
            <ListItem>
              <ListItemText primary="No Markdown" />
            </ListItem>
            <ListItem>
              <ListItemText primary="No Checksum/Unique" />
            </ListItem>
          </List>
        </Box>
      );
    }

    if (processingLevel === "level 2") {
      return (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 1 }}
          >
            Quick Plus: Current ExtractIQ full process
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Convert each page into image" />
            </ListItem>
            <ListItem>
              <ListItemText primary="No Markdown" />
            </ListItem>
            <ListItem>
              <ListItemText primary="No Checksum" />
            </ListItem>
            <ListItem>
              <ListItemText primary="No Unique" />
            </ListItem>
          </List>
        </Box>
      );
    }

    if (processingLevel === "level 4") {
      return (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", mb: 1 }}
          >
            Ultra: Current ExtractIQ full process
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Full Markdown" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Full Checksum" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Full Unique" />
            </ListItem>
          </List>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      {/* Upload Section and Search - Same Row */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        px: 2,
        flexWrap: 'wrap',
        gap: 1,
        marginRight: 5
      }}>
        {/* Left Side - CSV Buttons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Upload CSV */}
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={handleDragAndDrop}
            sx={{
              backgroundColor: '#1976D2',
              textTransform: 'none',
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              padding: { xs: '6px 12px', sm: '6px 14px', md: '6px 16px' },
              minHeight: 40,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#1565C0',
              },
            }}
          >
            Upload CSV
          </Button>

          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />

          {/* Download CSV */}
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            sx={{
              backgroundColor: '#1976D2',
              textTransform: 'none',
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              padding: { xs: '6px 12px', sm: '6px 14px', md: '6px 16px' },
              minHeight: 40,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#1565C0',
              },
            }}
          >
            Download CSV
          </Button>

          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              fontFamily: "'Poppins', sans-serif"
            }}
          >
            <InputLabel id="processing-options-label">
              Processing Options
            </InputLabel>
            <Select
              ref={selectRef}
              labelId="processing-options-label"
              value={formDetails?.processing_options}
              label="Processing Options"
              onChange={handleProcessingChange}
              sx={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <MenuItem value="level 1">Quick</MenuItem>
              <MenuItem value="level 2">Quick Plus</MenuItem>
              <MenuItem value="level 4">Ultra</MenuItem>
            </Select>
          </FormControl>
          {/* Popover */}
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{ p: 2 }}
          >
            <Box sx={{ p: 2, maxWidth: 280, position: "relative" }}>
              {/* Close button */}
              <IconButton
                size="small"
                onClick={handlePopoverClose}
                sx={{ position: "absolute", top: 4, right: 4 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              {getPopoverContent()}
            </Box>
          </Popover>
          <FormControlLabel
            label="Metadata Analysis"
            control={
              <Checkbox
                size="small"
                checked={formDetails?.meta_data_analyser}
                onChange={handleMetadataChange}
                sx={{ fontFamily: "'Poppins', sans-serif" }}
              />
            }
          />
        </Box>

        {/* Right Side - Search and Add Button */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search…"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setEditingRowIndex(null);
            }}
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              backgroundColor: 'white',
              borderRadius: '6px',
              width: { xs: '200px', sm: '220px', md: '250px' },
            }}
          />

          <Button
            variant="contained"
            onClick={() => {
              setNewRowData({
                Unique: '',
                Checksum: '',
                keys: '',
                Description: '',
                Format: "String",
                Fields: '',
                Fields_Description: ''
              });
              setRows([]);
              setEditingRowIndex(null);
              setOpenAddDataDialog(true);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textTransform: 'none',
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
              padding: { xs: '6px 12px', sm: '6px 14px', md: '6px 16px' },
              minHeight: 40,
              borderRadius: '6px',
              gap: 1,
            }}
          >
            <AddCircleOutlineIcon sx={{
              color: 'white',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
            }} />
            <Typography sx={{
              color: 'white',
              fontWeight: 500,
              fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' }
            }}>Add</Typography>
          </Button>
        </Box>
      </Box>

      {/* Table Section */}
      <Box sx={{
        px: { xs: 1, sm: 2 },
        overflow: 'hidden',

        paddingRight: 0,
        marginRight: 5
      }}>
        <TableContainer
          component={Paper}
          className="table-container"
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'auto',
            marginRight: 0,
            paddingRight: 0
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976D2" }}>
                {Object.keys(csvHeaders)
                  .filter((col) => col !== "Fields" && col !== "Fields_Description")
                  .map((col, i) => (
                    <TableCell
                      key={col}
                      sx={{
                        ...getCellStyle(i),
                        pl: 3,
                      }}
                    >
                      {col}
                      <Tooltip title={csvHeaders[col]} arrow>
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ verticalAlign: "middle", marginLeft: "4px" }}
                        />
                      </Tooltip>
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ border: 'none', p: 0 }}>
                    <EmptyState />
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Radio
                        checked={Number(row.Unique) === 1}
                        onChange={() =>
                          dispatch(updateUniqueField({
                            index: row.originalIndex,
                            key: 'Unique'
                          }))
                        }
                        disabled={editingRowIndex !== row.originalIndex}
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={Number(row.Checksum) === 1}
                        onChange={(e) =>
                          dispatch(updateCsvData({
                            index: row.originalIndex,
                            key: 'Checksum',
                            value:
                              e.target.checked ? '1' : ''
                          }))
                        }
                        disabled={editingRowIndex !== row.originalIndex}
                      />
                    </TableCell>
                    <TableCell>
                      {editingRowIndex === row.originalIndex ? (
                        <TextareaAutosize
                          maxRows={1}
                          value={row['Keys']}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            dispatch(updateCsvData({ index: row.originalIndex, key: 'Keys', value: newValue }))
                          }}
                          style={{
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            resize: "vertical",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" style={{ padding: "8px" }}>
                          {row['Keys']}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 500 }}>
                      {editingRowIndex === row.originalIndex ? (
                        <TextareaAutosize
                          maxRows={3}
                          value={row['Description'].replace(/,/g, '')}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            dispatch(updateCsvData({ index: row.originalIndex, key: 'Description', value: newValue }))
                          }}
                          onKeyDown={(e) => {
                            if (e.key === ",") {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            resize: "vertical",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" style={{ padding: "8px", whiteSpace: "pre-wrap" }}>
                          {row['Description']}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 250 }}>
                      <>
                        <Box display="flex" alignItems="center">
                          <Select
                            value={row['Format'] || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              dispatch(updateCsvData({ index: row.originalIndex, key: 'Format', value: newValue }));
                              if (newValue !== 'String') {
                                dispatch(updateCsvData({ index: row.originalIndex, key: 'Checksum', value: '' }));
                                dispatch(updateCsvData({ index: row.originalIndex, key: 'Unique', value: '' }));
                              }
                            }}
                            variant="outlined"
                            size="small"
                            style={{
                              minWidth: 120,
                              height: 30,
                              borderRadius: 25,
                              fontSize: 12,
                            }}
                            disabled={editingRowIndex !== row.originalIndex}
                          >
                            {formatOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                          {(row['Format'] === "Nest" || row['Format'] === "Enum") && (
                            <Button
                              onClick={() => handleOpenDialog(row, row.originalIndex)}
                              variant="contained"
                              size="small"
                              style={{
                                marginLeft: "8px",
                                backgroundColor: editingRowIndex !== row.originalIndex ? "grey" : "#1976D2",
                                color: "white",
                                height: 25,
                                borderRadius: 25,
                                minWidth: 80,
                              }}
                              disabled={editingRowIndex !== row.originalIndex}
                            >
                              <p style={{ fontSize: 9, margin: 0 }}>Subfields</p>
                            </Button>
                          )}
                        </Box>
                      </>
                    </TableCell>
                    <TableCell>
                      <Tooltip title='Edit' arrow>
                        <IconButton size='small' onClick={() => setEditingRowIndex(row.originalIndex)}>
                          <EditIcon style={{ color: "blue" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Delete' arrow>
                        <IconButton
                          size='small'
                          onClick={() => {
                            setDeleteIndex(row.originalIndex);
                            setOpenConfirmDialog(true);
                          }}
                        >
                          <DeleteIcon style={{ color: "darkred" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination - Only show when there's data */}
        {csvData.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={csvData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Box>


      {/* Popup Dialog for Subfields */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        sx={{ "& .MuiDialog-paper": { width: "600px", maxWidth: "90%" } }}
      >
        <DialogTitle>
          Field Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelIcon />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveFields}
            sx={{
              position: "absolute",
              right: 50,
              top: 15,
              textTransform: "none",
            }}
          >
            Save
          </Button>
        </DialogTitle>

        <DialogContent>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976D2" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '40%' }}>
                  <b>Fields</b>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '60%' }}>
                  <b>Field Description</b>
                </TableCell>
                <TableCell sx={{ width: '40px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {dialogData.map((field, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextareaAutosize
                      maxRows={1}
                      value={field.field.replace(/,/g, "")}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, "");
                        const newData = [...dialogData];
                        newData[index].field = value;
                        setDialogData(newData);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ",") e.preventDefault();
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextareaAutosize
                      maxRows={3}
                      value={field.description.replace(/,/g, '')}
                      onChange={(e) => {
                        const newData = [...dialogData];
                        newData[index].description = e.target.value;
                        setDialogData(newData);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ",") {
                          e.preventDefault();
                        }
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        const newData = dialogData.filter((_, i) => i !== index);
                        setDialogData(newData);
                      }}
                    >
                      <RemoveCircleOutlineIcon style={{ color: "darkred" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Add Row Button */}
          <Button
            variant="outlined"
            sx={{ mt: 2, textTransform: "none" }}
            onClick={() =>
              setDialogData((prev) => [...prev, { field: "", description: "" }])
            }
          >
            + Add Row
          </Button>
        </DialogContent>
      </Dialog>

      {/* Popup Dialog for Add Data */}
      <Dialog
        open={openAddDataDialog}
        onClose={() => setOpenAddDataDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: '1000px',
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => setOpenAddDataDialog(false)}
          sx={{
            position: 'absolute',
            right: 1,
            top: 1,
            color: '#555',
            zIndex: 2,
          }}
        >
          <CancelIcon />
        </IconButton>
        <DialogContent
          sx={{
            pt: 6,
            position: 'relative',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976D2" }}>
                  {Object.keys(csvHeaders)
                    .filter((col) => col !== "Fields" && col !== "Fields_Description" && col !== "Actions")
                    .map((col, i) => (
                      <TableCell
                        key={col}
                        sx={{
                          ...getCellStyle(i),
                          pl: 3,
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      icon={<RadioButtonUnchecked />}
                      checkedIcon={<RadioButtonChecked />}
                      checked={newRowData['Unique'] === '1'}
                      onChange={() => {
                        const isCurrentlySelected = newRowData['Unique'] === '1';
                        setNewRowData((prev) => ({
                          ...prev,
                          ['Unique']: isCurrentlySelected ? '' : '1',
                        }));
                      }}
                      disabled={newRowData['Format'] !== 'String'}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={Number(newRowData['Checksum']) === 1}
                      onChange={(e) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          ['Checksum']: e.target.checked ? '1' : '',
                        }))
                      }
                      disabled={newRowData['Format'] !== 'String'}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={newRowData['Keys'] || ''}
                      onChange={(e) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          ['Keys']: e.target.value,
                        }))
                      }
                      inputProps={{ style: { minWidth: '180px' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 500 }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={newRowData['Description'].replace(/,/g, '') || ''}
                      onChange={(e) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          ['Description']: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === ",") {
                          e.preventDefault();
                        }
                      }}
                      inputProps={{ style: { minWidth: '180px' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 250 }}>
                    <>
                      <Box display="flex" alignItems="center">
                        <Select
                          value={newRowData['Format'] || ''}
                          onChange={(e) => {
                            const newFormat = e.target.value;
                            setNewRowData((prev) => ({
                              ...prev,
                              Format: newFormat,
                              Checksum: prev.Checksum,
                              Unique: prev.Unique,
                            }));
                          }}
                          variant="outlined"
                          size="small"
                          style={{
                            minWidth: 120,
                            height: 30,
                            borderRadius: 25,
                            fontSize: 12,
                          }}
                        >
                          {formatOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {(newRowData['Format'] === "Nest" || newRowData['Format'] === "Enum") && (
                          <Button
                            onClick={() => setOpenAddSubfieldsDialog(true)}
                            variant="contained"
                            size="small"
                            style={{
                              marginLeft: "8px",
                              backgroundColor: "#1976D2",
                              color: "white",
                              height: 25,
                              borderRadius: 25,
                              minWidth: 80,
                            }}
                          >
                            <p style={{ fontSize: 9, margin: 0 }}>Subfields</p>
                          </Button>
                        )}
                      </Box>
                    </>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
            }}
            onClick={() => {
              if (!newRowData['Keys']) {
                toast.error("Key field should not be empty");
                return;
              }
              let updatedData = [];
              if (dataMode === "upload") {
                updatedData = [newRowData];
              } else {
                updatedData = [...csvData];
                if (newRowData.Unique === "1") {
                  updatedData = updatedData.map(row => ({
                    ...row,
                    Unique: ""
                  }));
                }
                updatedData.push(newRowData);
              }
              dispatch(setCsvData(updatedData));
              dispatch(setDataMode("manual"));
              setOpenAddDataDialog(false);
              setPage(0);
              toast.success("Field Added Successfully");
            }}

          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup Dialog for Add Subfields */}
      <Dialog
        open={openAddSubfieldsDialog}
        onClose={() => setOpenAddSubfieldsDialog(false)}
        sx={{ "& .MuiDialog-paper": { width: "600px", maxWidth: "90%" } }}
      >
        <DialogTitle>
          Field Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenAddSubfieldsDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelIcon />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveNewSubFields}
            sx={{
              position: "absolute",
              right: 50,
              top: 15,
              textTransform: "none",
            }}
          >
            Save
          </Button>
        </DialogTitle>

        <DialogContent>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976D2" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '40%' }}>
                  <b>Fields</b>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '60%' }}>
                  <b>Field Description</b>
                </TableCell>
                <TableCell sx={{ width: '40px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ width: '40%' }}>
                    <TextareaAutosize
                      maxRows={1}
                      value={row.field.replace(/,/g, "")}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, "");
                        const updatedRows = [...rows];
                        updatedRows[index].field = value;
                        setRows(updatedRows);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ",") e.preventDefault();
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '60%' }}>
                    <TextareaAutosize
                      maxRows={3}
                      value={row.description.replace(/,/g, '')}
                      onChange={(e) => {
                        const updatedRows = [...rows];
                        updatedRows[index].description = e.target.value;
                        setRows(updatedRows);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ",") {
                          e.preventDefault();
                        }
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleRemoveRow(index)}>
                      <RemoveCircleOutlineIcon style={{ color: "darkred" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              sx={{ textTransform: 'none' }}
            >
              Add Row
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Popup Dialog for Delete Confirmation Box */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this record? This process cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CsvFileScreen;
